/**
 * Sign-in.
 *
 * The address is asked for first, so the organisation's own sign-in methods
 * can be offered rather than a wall of provider buttons that may not apply.
 * What comes back decides the second step entirely: a password form, provider
 * buttons, or — for an organisation that runs both — both of them, because a
 * person with a password must never lose their way in just because their
 * colleagues use single sign-on.
 *
 * The composition is the approved "architectural" design: identity and
 * appearance across the top, the room drawn on the left, the form on the
 * right behind a thin rule. Its styles live under `.signin` in screens.css
 * rather than in the shared `.auth` block, which sign-up and the single
 * sign-on landing also use.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Endorsement, Lockup } from "@/components/Logo";
import { GoogleMark, MicrosoftMark } from "@/components/ProviderMark";
import { ApiError, api } from "@/lib/api";
import { useSession } from "@/lib/session";

interface Methods {
  local: boolean;
  microsoft: boolean;
  google: boolean;
  organization_name: string | null;
}

const APPEARANCES = ["light", "dark", "system"] as const;

export function SignIn() {
  const { signIn, theme, setTheme } = useSession();
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "method">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [methods, setMethods] = useState<Methods | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const providerRef = useRef<HTMLButtonElement>(null);

  // Focus follows the step, and where it lands depends on what the
  // organisation actually offers: a password field where there is one, the
  // first provider button where there is not. Leaving it on the button that
  // was just pressed strands anybody using a keyboard.
  useEffect(() => {
    if (step === "email") {
      emailRef.current?.focus();
      return;
    }
    if (methods?.local) passwordRef.current?.focus();
    else providerRef.current?.focus();
  }, [step, methods]);

  async function continueWithEmail(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<Methods>("/auth/methods", { email });
      setMethods(result);
      setStep("method");
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "We could not check that address. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { administers } = await signIn(email, password);
      // An administrator signing in is going to the administration area nine
      // times in ten. Landing them in room search and making them find a menu
      // is a small tax charged every single day.
      navigate(administers ? "/admin" : "/", { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Sign-in failed. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function startSso(provider: "microsoft" | "google") {
    setError(null);
    setBusy(true);
    try {
      const result = await api.get<{ authorize_url: string }>(
        `/auth/${provider}/start`,
        { email },
      );
      window.location.href = result.authorize_url;
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "That sign-in method is not available right now.",
      );
      setBusy(false);
    }
  }

  function useADifferentEmail() {
    setStep("email");
    setPassword("");
    setMethods(null);
    setError(null);
  }

  const sso = Boolean(methods?.microsoft || methods?.google);

  return (
    <div className="signin">
      <header className="signin__chrome">
        <Lockup height={30} />
        <div className="segmented" role="group" aria-label="Appearance">
          {APPEARANCES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={theme === option}
              onClick={() => setTheme(option)}
            >
              {option === "light" ? "Light" : option === "dark" ? "Dark" : "System"}
            </button>
          ))}
        </div>
      </header>

      <main className="signin__main">
        <section className="signin__story" aria-label="AlphaRoom">
          <p className="signin__eyebrow">A place to come together</p>
          <h1 className="signin__title">Space for better meetings.</h1>
          <p className="signin__story-copy">
            The right room.
            <br />
            The time to do good work.
          </p>
          <div className="signin__architecture" aria-hidden="true">
            <div className="signin__wall" />
            <div className="signin__wall signin__wall--opening" />
            <div className="signin__wall signin__wall--door" />
          </div>
        </section>

        <section className="signin__form-area" aria-label="Sign in">
          <h2 className="signin__welcome">Welcome to AlphaRoom.</h2>
          <p className="signin__instruction">
            {step === "email" ? (
              <>
                Start with your work e-mail.
                <br />
                We&rsquo;ll find your organisation&rsquo;s sign-in options.
              </>
            ) : methods?.organization_name ? (
              <>Signing in to {methods.organization_name}.</>
            ) : (
              <>Choose how you would like to sign in.</>
            )}
          </p>

          {error && (
            <div
              className="notice notice--error"
              role="alert"
              style={{ marginBottom: "var(--space-4)" }}
            >
              <span className="notice__bar" aria-hidden="true" />
              <div className="stack" style={{ gap: 2 }}>
                <p className="notice__title">Cannot sign in</p>
                <p className="notice__body">{error}</p>
              </div>
            </div>
          )}

          {step === "email" ? (
            <form className="signin__step" onSubmit={continueWithEmail}>
              <div className="field">
                {/* "Work e-mail", as every other screen in the product spells
                    it — the reference mock writes "Work email", and one screen
                    spelling it differently is how a product starts to look
                    assembled rather than designed. */}
                <label className="field__label" htmlFor="email">
                  Work e-mail
                </label>
                <input
                  id="email"
                  ref={emailRef}
                  className="signin__input"
                  type="email"
                  name="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                />
              </div>
              <button className="signin__primary" type="submit" disabled={busy}>
                <span>{busy ? "Checking…" : "Continue"}</span>
                <span aria-hidden="true">→</span>
              </button>
            </form>
          ) : (
            <div className="signin__step signin__step--arriving">
              <div className="signin__address">
                <span className="signin__address-value">{email}</span>
                <button type="button" className="signin__change" onClick={useADifferentEmail}>
                  Use a different email
                </button>
              </div>

              {methods?.microsoft && (
                <button
                  type="button"
                  ref={providerRef}
                  className="signin__provider"
                  onClick={() => startSso("microsoft")}
                  disabled={busy}
                >
                  <MicrosoftMark />
                  <span>Continue with Microsoft</span>
                </button>
              )}
              {methods?.google && (
                <button
                  type="button"
                  ref={methods.microsoft ? undefined : providerRef}
                  className="signin__provider"
                  onClick={() => startSso("google")}
                  disabled={busy}
                >
                  <GoogleMark />
                  <span>Continue with Google</span>
                </button>
              )}

              {methods?.local && (
                <>
                  {sso && <span className="signin__or">or</span>}
                  <form className="signin__step" onSubmit={submitPassword}>
                    {/* The address is in the form as well, hidden, so a
                        password manager files the password under the person
                        rather than under the page. */}
                    <input
                      type="email"
                      name="email"
                      value={email}
                      autoComplete="username"
                      readOnly
                      hidden
                    />
                    <div className="field">
                      <label className="field__label" htmlFor="password">
                        Password
                      </label>
                      <input
                        id="password"
                        ref={passwordRef}
                        className="signin__input"
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                      />
                    </div>
                    <button className="signin__primary" type="submit" disabled={busy}>
                      <span>{busy ? "Signing in…" : "Sign in"}</span>
                      <span aria-hidden="true">→</span>
                    </button>
                  </form>
                </>
              )}

              {!methods?.local && !sso && (
                <p className="signin__instruction" style={{ margin: 0 }}>
                  No sign-in method is enabled for this organisation. Ask your
                  AlphaRoom administrator to turn one on.
                </p>
              )}
            </div>
          )}

          {/* No link to sign-up: organisations are created by Alphabeta, and
              the endpoint refuses by default. An installation that turns it on
              is a demonstration server, and can reach /sign-up directly. */}
          <p className="signin__help">
            Need access? Contact your organisation&rsquo;s
            <br />
            AlphaRoom administrator.
          </p>
        </section>
      </main>

      <footer className="signin__footer">
        <Endorsement />
        <span>Built around your workplace.</span>
      </footer>
    </div>
  );
}
